import { Button, Flex } from 'antd';
import { useAccess } from '@sitemax/smx-permission';
import { useRef } from 'react';
import { RedoOutlined } from '@ant-design/icons';

import { Category, Document } from 'interfaces/documents';
import { useAppSelector } from 'utilities/hooks';
import FilterChips from './FilterChips';
import ActionDropdown from './ActionDropdown';
import FilterModal from 'modals/FilterModal';
import { BaseModalRef } from 'modals/BaseModal';
import { ModeDocumentsPageType } from '../../../types';
import { DocumentsToolbarButtonDelete } from './DocumentsToolbarButtonDelete';
import { DocumentsToolbarButtonRestore } from './DocumentsToolbarButtonRestore';
import { DocumentsToolbarUpload } from './DocumentsToolbarUpload';
import { DocumentsToolbarButtonCreateFolder } from './DocumentsToolbarButtonCreateFolder';
import { DocumentsToolbarButtonEmptyBin } from './DocumentsToolbarButtonEmptyBin';
import { DocumentsToolbarInputSearch } from './DocumentsToolbarInputSearch';
import { DocumentsToolbarButtonDownloads } from './DocumentsToolbarButtonDownloads';
import { MySharedDocumentsModal } from '../../../modals/my-shared-documents-modal/my-shared-documents';
import { useSelectorDevelopment } from '../../../hooks/useSelectorDevelopment';

const DocumentsToolbar = ({
    categoryId = null,
    fetchData,
    selected,
    modeDocument = 'documents',
}: {
    categoryId?: number | null;
    fetchData: () => void;
    selected: (Category | Document)[];
    modeDocument?: ModeDocumentsPageType;
}) => {
    const filterModal = useRef<BaseModalRef>(null);
    const { breadcrumbs } = useAppSelector((state) => state.documents);
    const { isDevelopmentMode } = useSelectorDevelopment();

    const isProjectDocument = breadcrumbs?.length > 0 ? breadcrumbs[breadcrumbs.length - 1].is_project_document : false;
    const canView = useAccess(isProjectDocument ? ['files.project.view'] : ['files.all.view']);
    const canManage = useAccess(isProjectDocument ? ['files.project.manage'] : ['files.all.manage']);
    const canCreate = useAccess(isProjectDocument ? ['files.project.create'] : ['files.all.create']);
    const canUseSharingModal = isDevelopmentMode;
    const isDeleted = modeDocument === 'deleted';
    const isDocuments = modeDocument === 'documents';

    return (
        <>
            {isDeleted && selected.length > 0 ? (
                <Flex style={{ marginBottom: '20px' }}>
                    <DocumentsToolbarButtonDelete selected={selected} isDisable={!canManage} />
                    <DocumentsToolbarButtonRestore selected={selected} isDisable={!canManage} />
                </Flex>
            ) : (
                <Flex justify="space-between" style={{ marginBottom: '20px' }} wrap="wrap" gap="10px">
                    <Flex>
                        {isDocuments && canCreate ? (
                            <>
                                <DocumentsToolbarUpload mode={'button'} fetchData={fetchData} />
                                <DocumentsToolbarButtonCreateFolder fetchData={fetchData} categoryId={categoryId} />
                            </>
                        ) : (
                            <></>
                        )}
                        {isDeleted && canManage ? <DocumentsToolbarButtonEmptyBin /> : <></>}
                        <DocumentsToolbarButtonDownloads />
                        <Button type="text" onClick={fetchData} icon={<RedoOutlined />}>
                            Refresh
                        </Button>
                    </Flex>
                    {canUseSharingModal && <MySharedDocumentsModal />}
                </Flex>
            )}
            {isDocuments ? <FilterChips fetchData={fetchData}></FilterChips> : <></>}
            {isDocuments ? (
                <Flex justify="space-between" style={{ marginBottom: '20px' }}>
                    <DocumentsToolbarInputSearch
                        fetchData={fetchData}
                        onOpenFilters={() => filterModal.current?.open()}
                    />
                    {(canManage || canView) && <ActionDropdown selected={selected} fetchData={fetchData} />}
                </Flex>
            ) : (
                <></>
            )}

            <FilterModal ref={filterModal} onSubmit={fetchData}></FilterModal>
        </>
    );
};

export default DocumentsToolbar;
